import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Helpers
import mq from '~/App/helpers/mq';
import { spacer } from '~/App/helpers/mixins';

// Shared components
import Container from '~/App/shared/components/Container';
import { BlockBackground } from '~/App/shared/components/BlockBackground';
import { useMatchMedia } from '~/App/shared/hooks/use-match-media';
import { Heading } from './components/Heading';
import { Accordion } from './components/Accordion';
import { BackgroundColor, Padding, Width } from '~/types/Block';

const Spacer = styled.div`
  ${spacer};
`;

const TwoColumns = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

type ColumnProps = {
  columns?: number;
};

const Column = styled.div<ColumnProps>`
  margin: 0;
  flex-basis: 100%;

  ${mq<ColumnProps>('≥small')`
    flex-basis: ${({ columns }) =>
      columns === 2 ? 'calc(50% - 1rem)' : '100%'};
  `};
`;

type Page = {
  id: string;
  preview: {
    heading: string;
    preamble: string;
  };
};

type Props = {
  pages: Page[];
  width: Width;
  heading?: string;
  padding: Padding;
  backgroundColor: BackgroundColor;
  headingSettings: {
    fontSize: 'heading1' | 'heading2' | 'heading3' | 'sectionHeading';
    textAlign: string;
  };
  settings: {
    mobile: boolean;
    desktop: boolean;
    columns: number;
    faqSchema: boolean;
  };
};

export default function FAQ(props: Props) {
  const {
    pages = [],
    width,
    heading = '',
    padding,
    backgroundColor = 'none',
    headingSettings,
    settings
  } = props;
  const [isEnabled, setEnabled] = useState(false);

  const wrapper = useRef<HTMLDivElement | null>(null);

  const matches = useMatchMedia(
    [
      settings.mobile ? '(max-width: 48em)' : '',
      settings.desktop ? '(min-width: 48em)' : ''
    ]
      .filter((m) => m)
      .join(', ')
  );

  const enableAccordion = useCallback(() => {
    if (isEnabled) return;

    setEnabled(true);
  }, [isEnabled]);

  const disableAccordion = useCallback(() => {
    if (!isEnabled) return;

    setEnabled(false);
  }, [isEnabled]);

  const renderPage = (page: Page, index: number) => {
    return (
      <Accordion
        key={index}
        isEnabled={isEnabled}
        heading={page.preview.heading}
        content={page.preview.preamble}
      />
    );
  };

  useEffect(() => {
    matches ? enableAccordion() : disableAccordion();
  }, [disableAccordion, enableAccordion, matches]);

  const halfLength = Math.ceil(pages.length / 2);
  const firstPages = pages.slice(0, halfLength);
  const lastPages = pages.slice(halfLength, pages.length);

  return (
    <BlockBackground backgroundColor={backgroundColor}>
      <Container
        width={width}
        paddingTop={padding.top}
        paddingLeft="small"
        paddingRight="small"
        paddingBottom={padding.bottom}
      >
        {heading && <Heading {...headingSettings} children={heading} />}
        <Spacer ref={wrapper}>
          <TwoColumns>
            <Column
              columns={settings.columns}
              children={firstPages.map(renderPage)}
            />
            <Column
              columns={settings.columns}
              children={lastPages.map(renderPage)}
            />
          </TwoColumns>
        </Spacer>
      </Container>
    </BlockBackground>
  );
}
